import React, { useState, useEffect, useMemo } from "react";
import { Box, Stack, Typography, Divider, Select, MenuItem, Button } from "@mui/material";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import dayjs from "dayjs";
import SnackbarNotification from "../../../components/ui/SnackBarNotification.jsx";
import { columns } from "../utils/columns-creator.js";
import PaginationNavigation from "../components/PaginationNavigation.jsx";
import ClientListFilterControls from "../components/TableFilterControls.jsx";
import { DataGridWithTopScrollbarComponent, IncrementRowIDComponent } from "../components/ClientListParts.jsx";
import { useResponsive } from "../../../utils/mobileresponsive.js";
import ClientDetailPanel from "../components/ClientDetailPanel.jsx";
import useFetchAgentBoB from "../hooks/useFetchAgentBoB.js";
import ConfirmationModal from "../../../components/confirmation-modal.jsx";
import AgentBoBApi from "../../../api/AgentBoBApi";
import { generateButtonSx } from "../../../utils/elementor-style-override";

const LIMIT_DEFAULT = 10;

// Backend periods are "YYYY-MM".
const formatPeriodLabel = (period) => (period ? dayjs(`${period}-01`).format("MMMM YYYY") : "");

/**
 * @param {boolean} canExport Shows the per-cutoff Export button. Only the agent view passes it:
 *                            admins export from Settings › Data Export, which covers any agents
 *                            and cutoffs, so a second, narrower button here would just duplicate it.
 */
export default function ClientListShell({ userId = null, headerSlot = null, footerSlot = null, emptyText, canExport = false }) {
	const { isMobile } = useResponsive();

	const [filters, setFilters] = useState({
		search: "",
		policy_status: "",
		offset: 0,
		limit: LIMIT_DEFAULT,
		carrier: "",
		effective_date_from: "",
		effective_date_to: "",
		// Empty means "let the backend pick the agent's newest snapshot".
		period: "",
	});
	const [searchInput, setSearchInput] = useState("");
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
	const [selectedRow, setSelectedRow] = useState(null);
	const [exportOpen, setExportOpen] = useState(false);
	const [exporting, setExporting] = useState(false);

	const { rows, total, loading, issuers, statuses, applicantCount, periods, period } = useFetchAgentBoB({ userId, filters });

	const policyCount = total;
	const currentPage = Math.floor(filters.offset / filters.limit) + 1;
	const totalPages = Math.ceil(total / filters.limit);

	useEffect(() => {
		const handler = setTimeout(() => {
			setFilters((f) => ({ ...f, search: searchInput, offset: 0 }));
		}, 250);
		return () => clearTimeout(handler);
	}, [searchInput]);

	const handleNext = () => setFilters((f) => ({ ...f, offset: f.offset + f.limit }));
	const handlePrev = () => setFilters((f) => ({ ...f, offset: Math.max(0, f.offset - f.limit) }));
	const handleChangePerPage = (value) => setFilters((f) => ({ ...f, limit: value, offset: 0 }));

	const clearFilters = () => {
		setFilters((f) => ({ ...f, offset: 0 }));
		setSearchInput("");
	};

	const handlePeriodChange = (value) => {
		setFilters((f) => ({ ...f, period: value, offset: 0 }));
	};

	// The whole cutoff, regardless of the search and filters applied to the table.
	const handleExport = async () => {
		setExporting(true);

		try {
			const data = await AgentBoBApi.exportAgentBoB({ user_id: userId, period });

			if (!data.rows?.length) {
				setSnackbar({ open: true, message: "There are no policies in this cutoff to export.", severity: "info" });
				return;
			}

			// Loaded on demand: ExcelJS is large and most visits never export.
			const { default: exportBoBExcel } = await import("../utils/exportBoBExcel.js");
			await exportBoBExcel(data);
			setSnackbar({ open: true, message: `Exported the ${formatPeriodLabel(period)} cutoff.`, severity: "success" });
		} catch (error) {
			setSnackbar({ open: true, message: error.message || "The export failed. Please try again.", severity: "error" });
		} finally {
			setExporting(false);
			setExportOpen(false);
		}
	};

	return (
		<Box sx={{ mr: { xs: 0, md: 10 }, py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
			<Stack direction={{ xs: "column", lg: "row" }} alignItems="center !important" justifyContent={"space-between"} spacing={2} px={3}>
				<Typography variant={isMobile ? "h2" : "h4"} sx={{ fontWeight: "bold !important", color: "#1D54A2", letterSpacing: 4 }}>
					BOOK OF BUSINESS
				</Typography>
				<Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
					{periods.length > 0 && (
						<Stack direction="row" alignItems="center" gap={1}>
							<Typography fontSize={12} color="text.disabled" sx={{ whiteSpace: "nowrap" }}>
								Cutoff:
							</Typography>
							<Select
								size="small"
								value={period ?? ""}
								onChange={(e) => handlePeriodChange(e.target.value)}
								sx={{ fontSize: 13, minWidth: 150, backgroundColor: "#fff" }}
							>
								{periods.map((p) => (
									<MenuItem key={p} value={p} sx={{ fontSize: 13 }}>
										{formatPeriodLabel(p)}
									</MenuItem>
								))}
							</Select>
							{canExport && (
								<Button
									startIcon={<SystemUpdateAltIcon />}
									variant="contained"
									onClick={() => setExportOpen(true)}
									disabled={!period || loading || exporting}
									sx={{
										px: 2.5,
										whiteSpace: "nowrap",
										...generateButtonSx(
											{ bg: "#F4F4F5 !important", text: "#1D54A2" },
											{ bg: "#F4F4F5 !important", text: "#1D54A2" }
										),
									}}
								>
									Export
								</Button>
							)}
						</Stack>
					)}
					{headerSlot}
				</Stack>
			</Stack>

			<Box sx={{ mb: { xs: 0, sm: 3 }, mt: { xs: 3, sm: 3 } }}>
				<Divider />
			</Box>

			<Box sx={{ ml: { xs: 1, md: 4 } }}>
				<ClientListFilterControls
					filters={filters}
					setFilters={setFilters}
					clearFilters={clearFilters}
					searchTerm={searchInput}
					onSearchChange={(val) => setSearchInput(val)}
					applicantCount={applicantCount}
					policyCount={policyCount}
					carrierOptions={issuers}
					statusOptions={statuses}
				/>
			</Box>

			<Box sx={{ position: "relative", width: "100%" }}>
				<Box sx={{ position: "absolute", left: 0, top: 0, zIndex: 2, height: "100%", display: "flex", alignItems: "flex-start" }}>
					{!loading && <IncrementRowIDComponent rows={rows} loading={loading} />}
				</Box>

				<Box sx={{ display: "flex", flexDirection: "column", ml: { xs: "35px", md: "60px" } }}>
					<DataGridWithTopScrollbarComponent
						rows={rows}
						columns={columns}
						userType="admin"
						loading={loading}
						onRowClick={(params) => setSelectedRow(params.row)}
						emptyText={emptyText}
					/>

					<Stack
						alignItems={{ xs: "center !important", sm: "flex-end !important" }}
						justifyContent={{ xs: "center", sm: "flex-end !important" }}
						sx={{ border: "1px solid #ccc", borderRadius: 1, p: { xs: 2, sm: 0 }, gap: { xs: 2, sm: 0 } }}
					>
						<PaginationNavigation
							onChangePerPage={handleChangePerPage}
							onNext={handleNext}
							onPrev={handlePrev}
							currentPage={currentPage}
							totalPages={totalPages}
							page={filters.limit}
						/>
					</Stack>
				</Box>
			</Box>

			<ConfirmationModal
				open={canExport && exportOpen}
				onClose={() => !exporting && setExportOpen(false)}
				onConfirm={handleExport}
				title={`Export this ${formatPeriodLabel(period)} cutoff?`}
				description="Every policy in this cutoff will be downloaded as an Excel file, including each client's notes and document links. Search and filters are not applied."
				confirmText="Export"
				cancelText="Cancel"
				isLoading={exporting}
			/>

			<ClientDetailPanel open={!!selectedRow} onClose={() => setSelectedRow(null)} row={selectedRow} />

			<SnackbarNotification
				open={snackbar.open}
				message={snackbar.message}
				severity={snackbar.severity}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
			/>

			{footerSlot}
		</Box>
	);
}
